/**
 * SESAAT — Affiliate Service
 * Firestore CRUD for internal affiliate system (Refferq-inspired)
 * 
 * Collections: affiliates, referrals, commissions, referral_clicks,
 *              conversions, commission_rules, partner_groups, programs,
 *              payouts, transactions, email_templates, email_logs,
 *              resources, affiliate_audit_logs, affiliate_config
 */

import { db, auth, functions } from '../firebase';
import {
  doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc,
  collection, query, where, orderBy, limit, getDocs,
  onSnapshot, serverTimestamp, increment, runTransaction,
  getCountFromServer, writeBatch, startAfter
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// ═══════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════

/** Get ref cookie value */
export function getRefCookie() {
  const match = document.cookie.match(/(?:^|;\s*)sesaat_ref=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/** Resolve cookie domain for cross-subdomain tracking (supports sesaat.ai + sesaat.my) */
function resolveCookieDomain() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.endsWith('sesaat.ai')) return '.sesaat.ai';
  if (host.endsWith('sesaat.my')) return '.sesaat.my';
  return null;
}

/** Set ref cookie (30 days default) — cross-subdomain when on sesaat.ai / sesaat.my */
export function setRefCookie(refCode, days = 30) {
  if (!refCode) return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  const domain = resolveCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `sesaat_ref=${encodeURIComponent(refCode)}; expires=${expires.toUTCString()}; path=/${domainPart}; SameSite=Lax`;
}

/** Clear ref cookie (clear both scoped and cross-subdomain variants) */
export function clearRefCookie() {
  const domain = resolveCookieDomain();
  document.cookie = 'sesaat_ref=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
  if (domain) {
    document.cookie = `sesaat_ref=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax`;
  }
}

// ═══════════════════════════════════════════
// AFFILIATE PROFILE
// ═══════════════════════════════════════════

/** Join affiliate program — delegates to Cloud Function (server-enforced tier gate + refCode uniqueness) */
// eslint-disable-next-line no-unused-vars
export async function joinAffiliateProgram(userId, email, name, whatsappNumber = '', payoutTermsAccepted = false) {
  const claimFn = httpsCallable(functions, 'claimAffiliateAccess');
  try {
    const result = await claimFn({ whatsappNumber, payoutTermsAccepted });
    return result.data.affiliate;
  } catch (err) {
    const msg = err?.message || 'Gagal menghantar permohonan. Sila cuba lagi.';
    throw new Error(msg);
  }
}

/** Get affiliate profile by userId */
export async function getAffiliate(userId) {
  const snap = await getDoc(doc(db, 'affiliates', userId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Get affiliate by refCode (for tracking) */
export async function getAffiliateByRefCode(refCode) {
  const q = query(collection(db, 'affiliates'), where('refCode', '==', refCode), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const doc = snap.docs[0];
  return { id: doc.id, ...doc.data() };
}

/** Get affiliate display info by slug or refCode — via Cloud Function (rules block client query) */
export async function getAffiliateBySlug(slug) {
  if (!slug) return null;
  try {
    const fn = httpsCallable(functions, 'resolveAffiliateRef');
    const res = await fn({ ref: slug });
    if (!res.data?.found) return null;
    return {
      customSlug: res.data.customSlug,
      refCode: res.data.refCode,
      name: res.data.name || null,
      whatsappNumber: res.data.whatsappNumber || null,
      supportGroupLink: res.data.supportGroupLink || null
    };
  } catch {
    return null;
  }
}

// Master fallback group invite — used when a user's referring agent has no
// supportGroupLink set, or when the user has no referrer at all (direct signup).
// Dua group berasingan ikut tier. Link berbayar sama dengan yang dihantar dlm
// mesej WhatsApp selepas pembelian (utils/whatsappTemplate.js) dan page
// BillplzPaymentReturnView — jangan pecahkan; ubah di sini kalau nak tukar.
export const OFFICIAL_USER_SUPPORT_GROUP = 'https://chat.whatsapp.com/KWtRJr7FXtn1dEuNTlDpaw'; // Free trial
export const PAID_USER_SUPPORT_GROUP = 'https://chat.whatsapp.com/GJtccD9aKu12gWYgFtxsGr';     // Premium / Fighter

/**
 * Group rasmi ikut tier pengguna.
 * Nota: link group ejen yang merujuk (supportGroupLink) tetap diutamakan oleh
 * pemanggil — fungsi ni hanya tentukan fallback bila ejen tiada link.
 */
export function officialSupportGroup(isPaid) {
  return isPaid ? PAID_USER_SUPPORT_GROUP : OFFICIAL_USER_SUPPORT_GROUP;
}

/** Validate WhatsApp group invite link format. Empty string = clear. */
export function validateWASupportGroupLink(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { valid: true, normalized: '' };
  const re = /^https:\/\/chat\.whatsapp\.com\/[A-Za-z0-9_-]{8,}\/?$/;
  if (!re.test(trimmed)) {
    return { valid: false, error: 'Format link tak sah. Contoh: https://chat.whatsapp.com/AbCdEfGh123' };
  }
  return { valid: true, normalized: trimmed.replace(/\/$/, '') };
}

/** Update affiliate-side WhatsApp number — separate from users.whatsappNumber.
 *  Rules allow self-write on own affiliate doc; cleaned to digits-only to match
 *  existing format set by claimAffiliateAccess cloud function. */
export async function updateAffiliateWhatsapp(userId, raw) {
  const cleaned = String(raw || '').replace(/\D/g, '');
  if (cleaned.length < 9 || cleaned.length > 15) {
    throw new Error('Nombor WhatsApp tidak sah. Sila masukkan 9-15 digit.');
  }
  await updateDoc(doc(db, 'affiliates', userId), {
    whatsappNumber: cleaned,
    updatedAt: serverTimestamp()
  });
  return cleaned;
}

/** Update support group link on own affiliate doc (rules allow self-write) */
export async function updateSupportGroupLink(userId, link) {
  const v = validateWASupportGroupLink(link);
  if (!v.valid) throw new Error(v.error);
  await updateDoc(doc(db, 'affiliates', userId), {
    supportGroupLink: v.normalized || null,
    updatedAt: serverTimestamp()
  });
  return v.normalized;
}

/** Mark current user joined their referrer's support group — bumps ejen counter */
export async function markSupportGroupJoined() {
  const fn = httpsCallable(functions, 'markSupportGroupJoined');
  try {
    const res = await fn({});
    return res.data || { success: true };
  } catch (e) {
    // Soft-fail — joining shouldn't block UX even if counter write fails
    console.warn('[markSupportGroupJoined] failed:', e?.message);
    return { success: false };
  }
}

/** Sanitize slug input — shared between client-side preview + server-side save */
export function sanitizeSlug(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')    // strip invalid chars
    .replace(/^-+|-+$/g, '')        // strip leading/trailing dashes
    .replace(/-{2,}/g, '-');        // collapse multiple dashes
}

/** Update custom slug — brokered via Cloud Function (uniqueness check needs cross-user read) */
export async function updateCustomSlug(slug) {
  const clean = sanitizeSlug(slug);
  if (clean.length < 3 || clean.length > 30) throw new Error('Slug mesti antara 3-30 aksara (huruf/nombor/dash).');
  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(clean)) throw new Error('Slug mesti mula & tamat dengan huruf atau nombor.');

  const fn = httpsCallable(functions, 'updateAffiliateSlug');
  try {
    const res = await fn({ slug: clean });
    return res.data?.slug || clean;
  } catch (e) {
    throw new Error(e?.message || 'Gagal simpan slug.');
  }
}

/** Update payout/bank details — blocked if payout is currently processing */
export async function updatePayoutDetails(userId, details) {
  // Check if there's an active payout in processing — prevent bank detail changes mid-payout
  const activePayout = query(
    collection(db, 'payouts'),
    where('affiliateId', '==', userId),
    where('status', '==', 'processing'),
    limit(1)
  );
  const activeSnap = await getDocs(activePayout);
  if (!activeSnap.empty) {
    throw new Error('Tidak boleh menukar maklumat bank semasa payout sedang diproses. Sila tunggu sehingga selesai.');
  }

  await updateDoc(doc(db, 'affiliates', userId), {
    payoutDetails: details,
    updatedAt: serverTimestamp()
  });
}

/** Subscribe to real-time affiliate profile updates */
export function subscribeToAffiliate(userId, callback) {
  return onSnapshot(doc(db, 'affiliates', userId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}
// NOTE: getAffiliate() one-time fetch dah wujud di line ~74 — guna tu untuk pagination/refresh flow.

// ═══════════════════════════════════════════
// CLICK TRACKING
// ═══════════════════════════════════════════

/** Track a referral link click (with sliding cookie window + rate limit) */
export async function trackClick(refCode) {
  if (!refCode) return;
  try {
    // Extend cookie on every visit (sliding window — resets 30-day expiry)
    setRefCookie(refCode);

    // Rate limit: max 1 click record per refCode per 5 minutes (shared across tabs)
    const rateLimitKey = `sesaat_click_${refCode}`;
    const lastClick = localStorage.getItem(rateLimitKey);
    if (lastClick && Date.now() - parseInt(lastClick) < 5 * 60 * 1000) {
      return; // Already tracked recently — skip to avoid spam
    }
    localStorage.setItem(rateLimitKey, Date.now().toString());

    // Lookup affiliate first — skip if refCode doesn't belong to a real affiliate
    const affiliate = await getAffiliateByRefCode(refCode);
    if (!affiliate) {
      console.warn(`[Affiliate] Click ignored — refCode not found: ${refCode}`);
      return;
    }

    // Record click event (only for valid refCodes)
    await addDoc(collection(db, 'referral_clicks'), {
      refCode,
      affiliateId: affiliate.id,
      userAgent: navigator.userAgent || '',
      referer: document.referrer || '',
      metadata: {},
      createdAt: serverTimestamp()
    });

    await updateDoc(doc(db, 'affiliates', affiliate.id), {
      totalClicks: increment(1)
    });
  } catch (e) {
    console.warn('[Affiliate] Click tracking failed (non-fatal):', e.message);
  }
}

// ═══════════════════════════════════════════
// REFERRALS
// ═══════════════════════════════════════════

/** Get referrals for an affiliate (with optional cursor pagination)
 *  opts.maxResults: limit per batch (default 50)
 *  opts.startAfterDoc: pass last doc snapshot from previous batch for Load More
 *  Returns { items, lastDoc } — lastDoc untuk pass back next call
 */
export async function getReferrals(affiliateUserId, opts = {}) {
  const maxResults = opts.maxResults || 50;
  const constraints = [
    where('referrerId', '==', affiliateUserId),
    orderBy('createdAt', 'desc')
  ];
  if (opts.startAfterDoc) constraints.push(startAfter(opts.startAfterDoc));
  constraints.push(limit(maxResults));
  const q = query(collection(db, 'referrals'), ...constraints);
  const snap = await getDocs(q);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs[snap.docs.length - 1] || null;
  return { items, lastDoc, hasMore: snap.docs.length === maxResults };
}

/** Get total referrals count for affiliate — 1 read regardless of doc count (cheap) */
export async function getReferralsCount(affiliateUserId) {
  const q = query(
    collection(db, 'referrals'),
    where('referrerId', '==', affiliateUserId)
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/** Subscribe to real-time referrals */
export function subscribeToReferrals(affiliateUserId, callback) {
  const q = query(
    collection(db, 'referrals'),
    where('referrerId', '==', affiliateUserId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ═══════════════════════════════════════════
// COMMISSIONS
// ═══════════════════════════════════════════

/** Get commissions for an affiliate (with optional cursor pagination).
 *  Sama shape macam getReferrals — { items, lastDoc, hasMore }
 */
export async function getCommissions(affiliateUserId, opts = {}) {
  const maxResults = opts.maxResults || 50;
  const constraints = [
    where('affiliateId', '==', affiliateUserId),
    orderBy('createdAt', 'desc')
  ];
  if (opts.startAfterDoc) constraints.push(startAfter(opts.startAfterDoc));
  constraints.push(limit(maxResults));
  const q = query(collection(db, 'commissions'), ...constraints);
  const snap = await getDocs(q);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  const lastDoc = snap.docs[snap.docs.length - 1] || null;
  return { items, lastDoc, hasMore: snap.docs.length === maxResults };
}

/** Get total commissions count — 1 read */
export async function getCommissionsCount(affiliateUserId) {
  const q = query(
    collection(db, 'commissions'),
    where('affiliateId', '==', affiliateUserId)
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/** Get lifetime sales total — full fetch of ALL commissions (no cap), sum of amount/rate.
 *  Page-based `commissions` state cuma pegang 50 item semasa — jangan guna untuk jumlah lifetime. */
export async function getLifetimeSalesTotal(affiliateUserId) {
  const q = query(
    collection(db, 'commissions'),
    where('affiliateId', '==', affiliateUserId)
  );
  const snap = await getDocs(q);
  return snap.docs.reduce((sum, d) => {
    const c = d.data();
    if (!c.rate || c.rate <= 0) return sum;
    return sum + (c.amount || 0) / c.rate;
  }, 0);
}

/** Subscribe to real-time commissions */
export function subscribeToCommissions(affiliateUserId, callback) {
  const q = query(
    collection(db, 'commissions'),
    where('affiliateId', '==', affiliateUserId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ═══════════════════════════════════════════
// PAYOUTS
// ═══════════════════════════════════════════

/** Request a payout */
export async function requestPayout(userId) {
  const affiliate = await getAffiliate(userId);
  if (!affiliate) throw new Error('Affiliate profile not found');
  if (affiliate.unpaidBalance <= 0) throw new Error('Tiada baki untuk pembayaran');

  // ═══ BANK DETAILS CHECK — wajib isi sebelum request payout ═══
  const pd = affiliate.payoutDetails || {};
  const bankName = (pd.bankName || '').trim();
  const accountNumber = (pd.accountNumber || '').replace(/\s/g, '');
  const accountHolder = (pd.accountHolder || '').trim();
  if (!bankName || !/^\d{8,20}$/.test(accountNumber) || accountHolder.length < 3) {
    throw new Error('Sila isi maklumat bank (nama bank, nombor akaun, pemegang akaun) di tab Settings sebelum request payout.');
  }

  // Get config for minimum threshold
  const configSnap = await getDoc(doc(db, 'affiliate_config', 'settings'));
  const config = configSnap.exists() ? configSnap.data() : {};
  const minThreshold = config.minPayoutThreshold || 5000;

  if (affiliate.unpaidBalance < minThreshold) {
    const minRM = (minThreshold / 100).toFixed(2);
    throw new Error(`Baki minimum untuk payout ialah RM${minRM}`);
  }

  // ═══ DUPLICATE PAYOUT CHECK — block if pending/processing payout already exists ═══
  const existingPayouts = query(
    collection(db, 'payouts'),
    where('affiliateId', '==', userId),
    where('status', 'in', ['pending', 'processing']),
    limit(1)
  );
  const existingSnap = await getDocs(existingPayouts);
  if (!existingSnap.empty) {
    throw new Error('Anda sudah mempunyai permintaan payout yang belum selesai. Sila tunggu sehingga ia diproses.');
  }

  // Create payout request
  // ⚠️ KEMBAR: firestore.rules payouts `create` menyemak medan ini satu persatu (hasOnly +
  //    amount == unpaidBalance + bankDetails == payoutDetails) — ubah di sini = ubah di sana.
  await addDoc(collection(db, 'payouts'), {
    affiliateId: userId,
    userId,
    // ── Snapshot identiti ejen pada masa request (macam bankDetails) ──
    // Supaya payout jadi rekod berdiri sendiri — admin nampak nama/AFR/email
    // tanpa perlu cari balik rekod ejen (yang boleh tertolak keluar had 100).
    affiliateName: affiliate.name || '',
    affiliateEmail: affiliate.email || '',
    affiliateRefCode: affiliate.refCode || '',
    amount: affiliate.unpaidBalance,
    commissionCount: 0, // admin will link commissions
    commissionIds: [],
    method: 'bank_transfer',
    status: 'pending',
    bankDetails: affiliate.payoutDetails || {},
    notes: '',
    processedBy: null,
    processedAt: null,
    createdAt: serverTimestamp()
  });

  return true;
}

/** Get payouts for an affiliate */
export async function getPayouts(affiliateUserId, maxResults = 100) {
  const q = query(
    collection(db, 'payouts'),
    where('affiliateId', '==', affiliateUserId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══════════════════════════════════════════
// TRANSACTIONS
// ═══════════════════════════════════════════

/** Get transactions for an affiliate */
export async function getTransactions(affiliateUserId, maxResults = 50) {
  const q = query(
    collection(db, 'transactions'),
    where('affiliateId', '==', affiliateUserId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══════════════════════════════════════════
// CONVERSIONS
// ═══════════════════════════════════════════

/** Get conversions for an affiliate */
export async function getConversions(affiliateUserId, maxResults = 50) {
  const q = query(
    collection(db, 'conversions'),
    where('affiliateId', '==', affiliateUserId),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══════════════════════════════════════════
// CONFIG & RULES (Read-only for affiliates)
// ═══════════════════════════════════════════

/** Get affiliate program config */
export async function getAffiliateConfig() {
  const snap = await getDoc(doc(db, 'affiliate_config', 'settings'));
  return snap.exists() ? snap.data() : null;
}


/** Get commission rules */
export async function getCommissionRules() {
  const q = query(
    collection(db, 'commission_rules'),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Get partner groups */
export async function getPartnerGroups() {
  const q = query(collection(db, 'partner_groups'), orderBy('name'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Get programs */
export async function getPrograms() {
  const q = query(collection(db, 'programs'), where('isActive', '==', true));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══════════════════════════════════════════
// RESOURCES (Marketing assets for affiliates)
// ═══════════════════════════════════════════

/** Get active resources */
export async function getResources() {
  const q = query(
    collection(db, 'resources'),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══════════════════════════════════════════
// ADMIN FUNCTIONS
// ═══════════════════════════════════════════

/** [Admin] Get all affiliates */
export async function adminGetAllAffiliates(maxResults = 2000) {
  const q = query(collection(db, 'affiliates'), orderBy('joinedAt', 'desc'), limit(maxResults));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * [Admin] Tarik rekod ejen tertentu ikut ID.
 * Guna untuk resolve ejen yang TAK masuk senarai adminGetAllAffiliates
 * (cth ejen lama yang minta payout tapi dah tertolak keluar had). Cuma tarik
 * yang perlu sahaja (jimat bacaan) — ID yang tak wujud/gagal dilangkau senyap.
 */
export async function adminGetAffiliatesByIds(ids = []) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (unique.length === 0) return [];
  const snaps = await Promise.all(
    unique.map(id => getDoc(doc(db, 'affiliates', id)).catch(() => null))
  );
  return snaps.filter(s => s && s.exists()).map(s => ({ id: s.id, ...s.data() }));
}

/** [Admin] Get all referrals (latest first) — siapa daftar via ref code, sama ada dah convert atau belum */
export async function adminGetAllReferrals(maxResults = 3000) {
  const q = query(collection(db, 'referrals'), orderBy('createdAt', 'desc'), limit(maxResults));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** [Admin] Get all active payouts (pending + processing) — admin perlu tengok kedua-dua untuk selesaikan flow */
export async function adminGetPendingPayouts() {
  const q = query(
    collection(db, 'payouts'),
    where('status', 'in', ['pending', 'processing']),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** [Admin] Get payout history (completed + failed) — untuk rujukan, tak boleh diedit */
export async function adminGetPayoutHistory(maxResults = 500) {
  const q = query(
    collection(db, 'payouts'),
    where('status', 'in', ['completed', 'failed']),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** [Admin] Approve payout — with minimum threshold enforcement */
export async function adminApprovePayout(payoutId, adminId) {
  // Validate payout amount meets minimum threshold
  const payoutSnap = await getDoc(doc(db, 'payouts', payoutId));
  if (!payoutSnap.exists()) throw new Error('Payout tidak dijumpai');
  const payout = payoutSnap.data();

  if (payout.status !== 'pending') {
    throw new Error(`Payout ini sudah dalam status: ${payout.status}`);
  }

  const configSnap = await getDoc(doc(db, 'affiliate_config', 'settings'));
  const config = configSnap.exists() ? configSnap.data() : {};
  const minThreshold = config.minPayoutThreshold || 5000;

  if (payout.amount < minThreshold) {
    const minRM = (minThreshold / 100).toFixed(2);
    throw new Error(`Jumlah payout RM${(payout.amount / 100).toFixed(2)} di bawah minimum RM${minRM}`);
  }

  await updateDoc(doc(db, 'payouts', payoutId), {
    status: 'processing',
    processedBy: adminId,
    processedAt: serverTimestamp()
  });
  // Audit log
  await addDoc(collection(db, 'affiliate_audit_logs'), {
    actorId: adminId,
    actorEmail: auth.currentUser?.email || null,
    action: 'payout.approved',
    objectType: 'payout',
    objectId: payoutId,
    payload: { amount: payout.amount },
    createdAt: serverTimestamp()
  });
}

/** [Admin] Complete payout — atomic transaction with validation */
export async function adminCompletePayout(payoutId, affiliateId, adminId) {
  let actualAmount = 0;
  await runTransaction(db, async (transaction) => {
    const payoutRef = doc(db, 'payouts', payoutId);
    const payoutSnap = await transaction.get(payoutRef);
    if (!payoutSnap.exists()) throw new Error('Payout tidak dijumpai');

    const payout = payoutSnap.data();
    if (payout.status === 'completed') throw new Error('Payout ini sudah selesai');
    if (payout.status === 'failed') throw new Error('Payout ini sudah ditolak');

    const payoutAmount = payout.amount;
    actualAmount = payoutAmount;

    const affiliateRef = doc(db, 'affiliates', affiliateId);
    const affiliateSnap = await transaction.get(affiliateRef);
    if (!affiliateSnap.exists()) throw new Error('Affiliate tidak dijumpai');

    const affiliate = affiliateSnap.data();
    if ((affiliate.unpaidBalance || 0) < payoutAmount) {
      throw new Error(`Baki tidak mencukupi. Baki: ${affiliate.unpaidBalance}, Payout: ${payoutAmount}`);
    }

    transaction.update(payoutRef, {
      status: 'completed',
      processedAt: serverTimestamp()
    });

    transaction.update(affiliateRef, {
      unpaidBalance: increment(-payoutAmount),
      updatedAt: serverTimestamp()
    });
  });

  await addDoc(collection(db, 'affiliate_audit_logs'), {
    actorId: adminId,
    actorEmail: auth.currentUser?.email || null,
    action: 'payout.completed',
    objectType: 'payout',
    objectId: payoutId,
    payload: { amount: actualAmount, affiliateId },
    createdAt: serverTimestamp()
  });
}

/** [Admin] Reject payout */
export async function adminRejectPayout(payoutId, adminId, notes = '') {
  const payoutSnap = await getDoc(doc(db, 'payouts', payoutId));
  if (!payoutSnap.exists()) throw new Error('Payout tidak dijumpai');
  const payout = payoutSnap.data();
  if (payout.status === 'completed') throw new Error('Payout ini sudah selesai — tidak boleh ditolak');
  if (payout.status === 'failed') throw new Error('Payout ini sudah ditolak sebelum ini');

  await updateDoc(doc(db, 'payouts', payoutId), {
    status: 'failed',
    notes,
    processedBy: adminId,
    processedAt: serverTimestamp()
  });
  await addDoc(collection(db, 'affiliate_audit_logs'), {
    actorId: adminId,
    actorEmail: auth.currentUser?.email || null,
    action: 'payout.rejected',
    objectType: 'payout',
    objectId: payoutId,
    payload: { notes },
    createdAt: serverTimestamp()
  });
}

/** [Admin] Update affiliate config — logs rate changes for audit trail */
export async function adminUpdateConfig(configData, adminId = null) {
  // If commissionRate is being changed, log the old rate for history
  if (configData.commissionRate !== undefined) {
    const currentSnap = await getDoc(doc(db, 'affiliate_config', 'settings'));
    const current = currentSnap.exists() ? currentSnap.data() : {};
    if (current.commissionRate !== undefined && current.commissionRate !== configData.commissionRate) {
      await addDoc(collection(db, 'affiliate_audit_logs'), {
        actorId: adminId || auth.currentUser?.uid || null,
        actorEmail: auth.currentUser?.email || null,
        action: 'config.commission_rate_changed',
        objectType: 'config',
        objectId: 'settings',
        payload: {
          oldRate: current.commissionRate,
          newRate: configData.commissionRate
        },
        createdAt: serverTimestamp()
      });
    }
  }

  await setDoc(doc(db, 'affiliate_config', 'settings'), {
    ...configData,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

/** [Admin] Create/update commission rule — validates required fields */
export async function adminSaveCommissionRule(ruleId, ruleData) {
  if (!ruleData?.name || typeof ruleData.name !== 'string' || !ruleData.name.trim()) {
    throw new Error('Commission rule mesti ada `name` (string).');
  }
  if (typeof ruleData.isActive !== 'boolean') {
    throw new Error('Commission rule mesti ada `isActive` (true/false).');
  }
  const rate = Number(ruleData.rate);
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    throw new Error('Commission rule `rate` mesti antara 0 dan 1 (contoh 0.25 = 25%).');
  }

  const sanitized = { ...ruleData, name: ruleData.name.trim(), rate };

  if (ruleId) {
    await updateDoc(doc(db, 'commission_rules', ruleId), {
      ...sanitized,
      updatedAt: serverTimestamp()
    });
  } else {
    await addDoc(collection(db, 'commission_rules'), {
      ...sanitized,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
}

/** [Admin] Create/update partner group — blocks duplicate names (case-insensitive) */
export async function adminSavePartnerGroup(groupId, groupData) {
  const name = String(groupData?.name || '').trim();
  if (!name) throw new Error('Nama group wajib diisi.');

  // Uniqueness check: reject if another group uses the same name
  const existing = await getDocs(query(collection(db, 'partner_groups'), where('name', '==', name), limit(2)));
  const dup = existing.docs.find(d => d.id !== groupId);
  if (dup) throw new Error(`Group dengan nama "${name}" sudah wujud.`);

  const payload = { ...groupData, name };
  if (groupId) {
    await updateDoc(doc(db, 'partner_groups', groupId), {
      ...payload,
      updatedAt: serverTimestamp()
    });
  } else {
    await addDoc(collection(db, 'partner_groups'), {
      ...payload,
      affiliateCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
}

/** [Admin] Delete partner group — blocked if group still has affiliates assigned */
export async function adminDeletePartnerGroup(groupId) {
  const assigned = query(
    collection(db, 'affiliates'),
    where('partnerGroupId', '==', groupId),
    limit(1)
  );
  const snap = await getDocs(assigned);
  if (!snap.empty) {
    throw new Error('Group ini masih ada affiliate. Pindahkan mereka ke group lain dulu.');
  }
  await deleteDoc(doc(db, 'partner_groups', groupId));
}

/** [Admin] Count pending commissions (server-side count — cheap, doesn't load docs) */
export async function adminGetPendingCommissionCount() {
  const q = query(collection(db, 'commissions'), where('status', '==', 'pending'));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/** [Admin] Assign affiliate to partner group */
export async function adminAssignPartnerGroup(affiliateId, groupId) {
  await runTransaction(db, async (tx) => {
    const affRef = doc(db, 'affiliates', affiliateId);
    const affSnap = await tx.get(affRef);
    const oldGroupId = affSnap.exists() ? (affSnap.data().partnerGroupId || null) : null;

    tx.update(affRef, { partnerGroupId: groupId || null, updatedAt: serverTimestamp() });

    if (oldGroupId && oldGroupId !== groupId) {
      tx.update(doc(db, 'partner_groups', oldGroupId), { affiliateCount: increment(-1) });
    }
    if (groupId && groupId !== oldGroupId) {
      tx.update(doc(db, 'partner_groups', groupId), { affiliateCount: increment(1) });
    }
  });
}

/** [Admin] Create/update program */
export async function adminSaveProgram(programId, programData) {
  if (programId) {
    await updateDoc(doc(db, 'programs', programId), {
      ...programData,
      updatedAt: serverTimestamp()
    });
  } else {
    await addDoc(collection(db, 'programs'), {
      ...programData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
}

/** [Admin] Create/update email template */
export async function adminSaveEmailTemplate(templateId, templateData) {
  if (templateId) {
    await updateDoc(doc(db, 'email_templates', templateId), {
      ...templateData,
      updatedAt: serverTimestamp()
    });
  } else {
    await addDoc(collection(db, 'email_templates'), {
      ...templateData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
}

/** [Admin] Create/update resource */
export async function adminSaveResource(resourceId, resourceData) {
  if (resourceId) {
    await updateDoc(doc(db, 'resources', resourceId), {
      ...resourceData,
      updatedAt: serverTimestamp()
    });
  } else {
    await addDoc(collection(db, 'resources'), {
      ...resourceData,
      downloads: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  }
}

/** [Admin] Approve a pending commission (pending → approved) — atomic */
export async function adminApproveCommission(commissionId, adminId) {
  let commAmount = 0;
  let commAffiliateId = null;

  await runTransaction(db, async (transaction) => {
    const commRef = doc(db, 'commissions', commissionId);
    const commSnap = await transaction.get(commRef);
    if (!commSnap.exists()) throw new Error('Komisen tidak dijumpai');

    const comm = commSnap.data();
    if (comm.status !== 'pending') {
      throw new Error(`Komisen sudah dalam status: ${comm.status}`);
    }

    const affiliateRef = doc(db, 'affiliates', comm.affiliateId);
    const affiliateSnap = await transaction.get(affiliateRef);
    if (!affiliateSnap.exists()) {
      throw new Error('Affiliate tidak dijumpai — komisen tidak boleh diluluskan');
    }

    transaction.update(commRef, {
      status: 'approved',
      approvedAt: serverTimestamp()
    });

    const affUpdate = {
      unpaidBalance: increment(comm.amount),
      totalEarnings: increment(comm.amount),
      updatedAt: serverTimestamp()
    };
    // Komisen tier-2 (override) — jejak berasingan supaya kolum "Override Earned" tepat
    if (comm.tier === 2) affUpdate.overrideEarnings = increment(comm.amount);
    transaction.update(affiliateRef, affUpdate);

    commAmount = comm.amount;
    commAffiliateId = comm.affiliateId;
  });

  await addDoc(collection(db, 'affiliate_audit_logs'), {
    actorId: adminId,
    actorEmail: auth.currentUser?.email || null,
    action: 'commission.approved',
    objectType: 'commission',
    objectId: commissionId,
    payload: { amount: commAmount, affiliateId: commAffiliateId },
    createdAt: serverTimestamp()
  });
}

/** [Admin] Bulk approve all pending commissions for an affiliate — atomic */
export async function adminApproveAllCommissions(affiliateId, adminId) {
  const q = query(
    collection(db, 'commissions'),
    where('affiliateId', '==', affiliateId),
    where('status', '==', 'pending')
  );
  const snap = await getDocs(q);
  if (snap.docs.length === 0) return 0;

  const commissionIds = snap.docs.map(d => d.id);
  const initialCount = commissionIds.length;
  // Firestore transaction cap: 500 operations (reads + writes). We do 1 affiliate read
  // + N commission reads + N commission writes + 1 affiliate write = 2N+2. Safe limit ~240.
  if (commissionIds.length > 240) {
    throw new Error(`Terlalu banyak komisen pending (${commissionIds.length}). Sila luluskan secara berkelompok.`);
  }

  let approvedCount = 0;
  let totalAmount = 0;

  await runTransaction(db, async (transaction) => {
    const affiliateRef = doc(db, 'affiliates', affiliateId);
    const affiliateSnap = await transaction.get(affiliateRef);
    if (!affiliateSnap.exists()) {
      throw new Error('Affiliate tidak dijumpai — komisen tidak boleh diluluskan');
    }

    // Re-read commissions inside transaction to filter out any that changed status
    const commSnaps = await Promise.all(
      commissionIds.map(id => transaction.get(doc(db, 'commissions', id)))
    );
    const pending = commSnaps
      .filter(s => s.exists() && s.data().status === 'pending')
      .map(s => ({ ref: s.ref, amount: s.data().amount || 0, tier: s.data().tier || 1 }));

    if (pending.length === 0) {
      approvedCount = 0;
      totalAmount = 0;
      return;
    }

    // overrideTotal di-declare DALAM callback supaya reset tiap retry transaksi (elak double-count)
    let overrideTotal = 0;
    for (const { ref, amount, tier } of pending) {
      transaction.update(ref, {
        status: 'approved',
        approvedAt: serverTimestamp()
      });
      totalAmount += amount;
      if (tier === 2) overrideTotal += amount;
    }
    approvedCount = pending.length;

    const affUpdate = {
      unpaidBalance: increment(totalAmount),
      totalEarnings: increment(totalAmount),
      updatedAt: serverTimestamp()
    };
    if (overrideTotal > 0) affUpdate.overrideEarnings = increment(overrideTotal);
    transaction.update(affiliateRef, affUpdate);
  });

  if (approvedCount > 0) {
    await addDoc(collection(db, 'affiliate_audit_logs'), {
      actorId: adminId,
      actorEmail: auth.currentUser?.email || null,
      action: 'commission.bulk_approved',
      objectType: 'commission',
      objectId: affiliateId,
      payload: { count: approvedCount, totalAmount, skipped: initialCount - approvedCount },
      createdAt: serverTimestamp()
    });
  }

  return { approved: approvedCount, skipped: initialCount - approvedCount };
}

/** [Admin] Suspend/activate affiliate — with cleanup on suspend */
export async function adminSetAffiliateStatus(affiliateId, status, adminId) {
  const updates = {
    status,
    updatedAt: serverTimestamp()
  };

  // On suspend: record timestamp + auto-reject any pending/processing payouts
  if (status === 'suspended') {
    updates.suspendedAt = serverTimestamp();

    const activePayouts = query(
      collection(db, 'payouts'),
      where('affiliateId', '==', affiliateId),
      where('status', 'in', ['pending', 'processing'])
    );
    const payoutSnap = await getDocs(activePayouts);
    // Atomic batch: all payout updates + audit logs commit together, or none at all.
    // Batch limit is 500 writes; each payout costs 2 writes (update + audit log).
    if (payoutSnap.docs.length > 0) {
      const batch = writeBatch(db);
      for (const d of payoutSnap.docs) {
        batch.update(doc(db, 'payouts', d.id), {
          status: 'failed',
          notes: 'Auto-rejected: Affiliate suspended',
          processedBy: adminId,
          processedAt: serverTimestamp()
        });
        batch.set(doc(collection(db, 'affiliate_audit_logs')), {
          actorId: adminId,
          actorEmail: auth.currentUser?.email || null,
          action: 'payout.auto_rejected',
          objectType: 'payout',
          objectId: d.id,
          payload: {
            reason: 'affiliate_suspended',
            affiliateId,
            amount: d.data().amount || 0,
            previousStatus: d.data().status
          },
          createdAt: serverTimestamp()
        });
      }
      await batch.commit();
    }
  }

  // On reactivate: clear suspendedAt
  if (status === 'active') {
    updates.suspendedAt = null;
  }

  await updateDoc(doc(db, 'affiliates', affiliateId), updates);
  await addDoc(collection(db, 'affiliate_audit_logs'), {
    actorId: adminId,
    actorEmail: auth.currentUser?.email || null,
    action: status === 'active' ? 'affiliate.activated' : 'affiliate.suspended',
    objectType: 'affiliate',
    objectId: affiliateId,
    payload: { status },
    createdAt: serverTimestamp()
  });
}

/** [Admin] Get audit logs */
export async function adminGetAuditLogs(maxResults = 50) {
  const q = query(
    collection(db, 'affiliate_audit_logs'),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** [Admin] Get all referral clicks stats */
export async function adminGetClickStats(refCode, maxResults = 100) {
  const q = query(
    collection(db, 'referral_clicks'),
    where('refCode', '==', refCode),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ═══════════════════════════════════════════
// STRIPE CHECKOUT (via Cloud Function)
// ═══════════════════════════════════════════

const createCheckoutSessionFn = httpsCallable(functions, 'createCheckoutSession');

/** Create Stripe Checkout session with affiliate tracking.
 *  Attribution precedence: explicit affiliateRef → cookie → userReferredBy (stored at signup) */
export async function createCheckoutSession(plan, affiliateRef = null, userReferredBy = null) {
  const refCode = affiliateRef || getRefCookie() || userReferredBy || null;
  const result = await createCheckoutSessionFn({
    plan,
    affiliateRef: refCode
  });
  return result.data; // { url: 'https://checkout.stripe.com/...' }
}
